import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ReportMetrics } from '@flakiness/server/common/reportMetrics.js';
import { CHECK, humanReadableBytes, humanReadableInteger } from '@flakiness/shared/common/utils.js';
import { Task } from '@lit/task';

@customElement('usage-totals')
export class UsageTotals extends LitElement {

  // Project pages leave this empty, since the highlighted tab already names the page.
  @property() heading = '';
  @property() description = 'Sizes and counts cover retained data. One report can contain many test results.';
  @property({ attribute: false }) metrics?: ReportMetrics.Counters[];

  private _metrics = new Task(this, {
    args: () => [this.metrics] as const,
    task: async ([metrics], { signal }) => {
      CHECK(metrics);

      let runs = 0;
      let testRuns = 0;
      let reportBytes = 0;
      let attachmentBytes = 0;
      for (const m of metrics) {
        runs += m.runsCount;
        testRuns += m.testsCount;
        reportBytes += m.reportBytes;
        attachmentBytes += m.attachmentBytes;
      }
      return { runs, testRuns, reportBytes, attachmentBytes };
    },
  });

  override render() {
    const totals = this._metrics.value;
    const loading = html`<sl-skeleton effect=pulse></sl-skeleton>`;
    return html`
      <section aria-labelledby=${this.heading ? 'usage-heading' : nothing}>
        <header>
          ${this.heading ? html`<h1 id=usage-heading>${this.heading}</h1>` : nothing}
          <p>${this.description}</p>
        </header>
        <div class=storage>
          <dl class="metric total">
            <dt><sl-visually-hidden>Total storage</sl-visually-hidden></dt>
            <dd>${totals ? humanReadableBytes(totals.reportBytes + totals.attachmentBytes) : loading}</dd>
          </dl>
          <dl class=details>
            <div class=metric>
              <dt>Report storage</dt>
              <dd>${totals ? humanReadableBytes(totals.reportBytes) : loading}</dd>
            </div>
            <div class=metric>
              <dt>Attachment storage</dt>
              <dd>${totals ? humanReadableBytes(totals.attachmentBytes) : loading}</dd>
            </div>
            <div class=metric>
              <dt>Reports</dt>
              <dd>${totals ? humanReadableInteger(totals.runs) : loading}</dd>
            </div>
            <div class=metric>
              <dt>Test results</dt>
              <dd>${totals ? humanReadableInteger(totals.testRuns) : loading}</dd>
            </div>
          </dl>
        </div>
      </section>
    `;
  }

  static styles = [css`
    :host {
      display: block;
    }

    section {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: start;
      gap: var(--sl-spacing-2x-large);
    }

    h1 {
      margin: 0;
      font-size: var(--sl-font-size-2x-large);
      font-weight: var(--sl-font-weight-semibold);
      line-height: 1.2;
    }

    p {
      margin: var(--sl-spacing-x-small) 0 0;
      line-height: 1.5;
      color: var(--sl-color-neutral-600);
    }

    dl, dd {
      margin: 0;
    }

    .metric {
      display: flex;
      flex-direction: column;
    }

    dt {
      color: var(--sl-color-neutral-500);
    }

    dd {
      order: -1;
      font-variant-numeric: tabular-nums;
      font-weight: var(--sl-font-weight-semibold);
      white-space: nowrap;
    }

    .total {
      text-align: left;
    }

    .total dd {
      font-size: var(--sl-font-size-4x-large);
      line-height: 1;
    }

    sl-skeleton {
      display: inline-block;
      width: 6ch;
      height: 1cap;
      vertical-align: baseline;
    }

    sl-skeleton::part(base) {
      min-height: 0;
    }

    .total sl-skeleton {
      width: 5ch;
    }

    .details {
      display: grid;
      grid-template-columns: max-content max-content;
      justify-content: start;
      gap: 0 var(--sl-spacing-small);
      margin-top: var(--sl-spacing-x-small);
    }

    .details .metric {
      display: grid;
      grid-column: 1 / -1;
      grid-template-columns: subgrid;
      align-items: baseline;
    }

    .details dt::after {
      content: ':';
    }

    .details dd {
      order: 0;
      color: var(--sl-color-neutral-700);
      font-size: inherit;
      line-height: 1.5;
    }

    @media (max-width: 640px) {
      section {
        grid-template-columns: 1fr;
        gap: var(--sl-spacing-large);
      }
    }
  `]
}
